import type { GetOfferDto, OfferUnderContractSubjectFinanceDto } from "@unreserved/sdk";
import type { CounterOffer, OfferSummaryData, PropertyCardData } from "@unreserved/types";
import { VENDOR_MANAGED_CAMPAIGN, formatCurrency, parsePriceAmount } from "@unreserved/utils";

import { formatListingType } from "@/app/_libs/utils/property-details/property-transformer";

export function createPropertyCardFromOffer(listing: GetOfferDto["listing"]): PropertyCardData {
  const listingData = listing as GetOfferDto["listing"] & {
    displayPrice?: boolean;
    priceText?: string | null;
    campaign_type?: string | null;
  };

  const displayPrice =
    typeof listingData.displayPrice === "boolean" ? listingData.displayPrice : true;

  const rawPriceText = listingData.priceText ?? null;
  const trimmedPriceText = rawPriceText == null ? "" : String(rawPriceText).trim();
  const priceText = trimmedPriceText === "" ? undefined : trimmedPriceText;

  const campaignKey = String(listingData.campaign ?? listingData.campaign_type ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");

  const amount = parsePriceAmount(listing.price);
  const price =
    displayPrice === false || amount === undefined
      ? ""
      : formatCurrency(amount, "AUD", "en-AU", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });

  return {
    imageSrc:
      typeof listing.property.media?.fileUrl === "string" ? listing.property.media.fileUrl : "",
    saleType: formatListingType(listing.listingType ?? ""),
    address:
      typeof listing.property.location?.displayAddress === "string"
        ? listing.property.location.displayAddress
        : "",
    price,
    priceText,
    displayPrice,
    isVendorManagedCampaign: campaignKey === VENDOR_MANAGED_CAMPAIGN,
  };
}

export function diffInDaysFromNow(targetIso?: string): string {
  if (!targetIso) return "";
  const target = new Date(targetIso);
  if (Number.isNaN(target.getTime())) return "";
  const now = new Date();
  const ms = target.getTime() - now.getTime();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  return String(days > 0 ? days : 0);
}

export function toOfferSummary(offer: GetOfferDto["offer"]): OfferSummaryData {
  const settlementDateIso =
    typeof offer.settlementDate === "string" ? (offer.settlementDate as string) : undefined;
  const expiresAtIso =
    typeof offer.expiresAt === "string" ? (offer.expiresAt as string) : undefined;

  const settlementTermDays = diffInDaysFromNow(settlementDateIso);

  const depositRaw = offer.depositAmount as unknown;
  const depositNumber = typeof depositRaw === "number" ? depositRaw : 0;

  const financeSubject = offer.contractSubjects?.find((s) => s.type === "finance") as
    | OfferUnderContractSubjectFinanceDto
    | undefined;
  const inspectionSubject = offer.contractSubjects?.find(
    (s) => s.type === "building_inspection",
  ) as { date?: string } | undefined;
  const specialSubject = offer.contractSubjects?.find((s) => s.type === "special_condition") as
    | { condition?: string }
    | undefined;

  const financeDays = financeSubject ? diffInDaysFromNow(financeSubject.date) : "";
  const rejectionReason = offer.rejectedReason;

  const offerPriceNum =
    Number((offer as { offerPrice?: number; offer_price?: number }).offerPrice) ||
    Number((offer as { offerPrice?: number; offer_price?: number }).offer_price) ||
    0;

  return {
    offerPrice: formatCurrency(offerPriceNum, "AUD", "en-AU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
    depositAmount: formatCurrency(depositNumber, "AUD", "en-AU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
    settlementTerm: settlementTermDays,
    offerExpiryDate: expiresAtIso ?? "",
    subjectToFinance: {
      date: financeDays,
      bank: (financeSubject?.bank as string) ?? "",
    },
    subjectToBuildingInspection: {
      date: inspectionSubject?.date ?? "",
    },
    subjectToSpecialConditions: specialSubject?.condition
      ? { description: specialSubject.condition }
      : {},
    rejectionReason: rejectionReason as unknown as string,
  };
}

export function toCounterOffer(
  offer: GetOfferDto["offer"] | NonNullable<GetOfferDto["originalOffer"]>,
): CounterOffer {
  const settlementDateIso =
    typeof offer.settlementDate === "string" ? (offer.settlementDate as string) : undefined;
  const expiresAtIso =
    typeof offer.expiresAt === "string" ? (offer.expiresAt as string) : undefined;

  const settlementTermDays = diffInDaysFromNow(settlementDateIso);

  const depositRaw = offer.depositAmount as unknown;
  const depositNumber = typeof depositRaw === "number" ? depositRaw : 0;

  return {
    offerAmount: formatCurrency(offer.offerPrice, "AUD", "en-AU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
    depositAmount: formatCurrency(depositNumber, "AUD", "en-AU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
    settlementTerm: settlementTermDays,
    offerExpiryDate: typeof expiresAtIso === "string" ? expiresAtIso : "",
  };
}
